import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import escapeStringRegexp from 'escape-string-regexp';

const defaultStringifier = (value) => (value === undefined || value === null ? '' : value.toString());

const identity = (value) => value;

/**
 * Builds the matcher for a single searchable column.
 * When the column declares a searchNormalizer, it is applied to both the search value and the
 * cell value, so values that only differ in formatting - e.g. the phone numbers
 * "+1 (555) 123-4567" and "5551234567" - still match each other.
 * @param {Object} column the column to match on
 * @param {string} searchValue the text typed by the user
 * @returns {Object} the column's searchRegex and its matches(row) predicate
 */
const buildColumnMatcher = (column, searchValue) => {
  const normalize = column.searchNormalizer || identity;
  const stringifier = column.stringifier || defaultStringifier;
  const normalizedSearchValue = normalize(searchValue);
  const searchRegex = new RegExp(escapeStringRegexp(normalizedSearchValue), 'i');

  /*
   * A search value that the normalizer empties out - e.g. a name typed while a digits-only
   * column is searched - would turn into a regex matching every row, so the column sits this
   * search out instead.
   */
  const isSearchable = !searchValue || !!normalizedSearchValue;

  return {
    searchRegex,
    matches: (row) => isSearchable && searchRegex.test(normalize(stringifier(row[column.field]) ?? '')),
  };
};

/**
 * Filters rows by a quick search value, matching it against every column that opts in with
 * quickSearch:true. Exported for testing - components should use the useQuickSearch hook.
 * The search value is trimmed before matching, since a value pasted into the search box often
 * carries surrounding whitespace that the user cannot see. Only the matching is trimmed - the
 * input keeps showing exactly what was typed, so a space can still be typed between two words.
 * @param {Array} unfilteredRows array of data, the rows of the table
 * @param {Array} columns array of columns, or of plain search descriptors for fields that the
 * table does not display
 * @param {string} searchValue the text typed by the user
 * @returns {Array} the rows matching the search value
 */
const filterRowsByQuickSearch = (unfilteredRows, columns, searchValue = '') => {
  const trimmedSearchValue = searchValue.trim();

  const searchableColumns = columns
    .filter((column) => column.quickSearch)
    .map((column) => ({ column, ...buildColumnMatcher(column, trimmedSearchValue) }));

  return unfilteredRows.reduce((resultRows, row) => {
    const matchingColumns = searchableColumns.filter(({ matches }) => matches(row));

    if (matchingColumns.length === 0) {
      return resultRows;
    }

    const rowWithFilteredColumns = matchingColumns.reduce((prevRow, { column, searchRegex }) => {
      // Custom Search function for each column
      if (column.cellSearchFunction) {
        const cellValue = column.cellSearchFunction(row, trimmedSearchValue, searchRegex);
        return { ...prevRow, ...cellValue };
      }
      return prevRow;
    }, row);

    return [...resultRows, rowWithFilteredColumns];
  }, []);
};

/**
 * Hook to use the QuickSearchToolbar in a DataGrid component
 * @param {Array} rows array of data, the rows of the table
 * @param {Array} columns array of columns. The columns to be used in the filter must have
 * the attribute quickSearch:true and, optionally, a stringifier function - turning the cell
 * value into the text to search - and/or a searchNormalizer function, applied to both the
 * search value and the cell text so that formatting differences do not prevent a match.
 * Fields the table does not display can be searched by appending plain descriptors -
 * e.g. { field, quickSearch: true } - to the columns passed here only.
 * The search value is trimmed before matching, while the toolbar keeps showing it as typed.
 * @returns an object with the filteredRows and the toolbarProps, to be used in the
 * DataGrid component, in the componentsProps prop
 */
const useQuickSearch = (unfilteredRows, columns) => {
  const [searchText, setSearchText] = useState('');
  const [filteredRows, setFilteredRows] = useState(unfilteredRows);

  const requestSearch = useCallback((searchValue = '') => {
    setSearchText(searchValue);
    setFilteredRows(filterRowsByQuickSearch(unfilteredRows, columns, searchValue));
  }, [
    columns,
    unfilteredRows,
  ]);

  // Note: the useEffect is used to update the filteredRows when the unfilteredRows change
  useEffect(() => {
    requestSearch(searchText);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unfilteredRows]);

  const onChange = useCallback(
    (event) => requestSearch(event.target.value),
    [requestSearch],
  );

  const clearSearch = useCallback(
    () => requestSearch(),
    [requestSearch],
  );

  const toolbarProps = useMemo(() => ({
    value: searchText,
    onChange,
    clearSearch,
  }), [
    searchText,
    onChange,
    clearSearch,
  ]);

  return useMemo(() => ({
    toolbarProps,
    filteredRows,
  }), [
    toolbarProps,
    filteredRows,
  ]);
};

export { filterRowsByQuickSearch };

export default useQuickSearch;
